import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { alpha, Box, Checkbox, Stack, Tooltip, Typography } from "@mui/material";
import { useMemo, type KeyboardEvent } from "react";
import {
  insightMetricRowLayoutSx,
  insightSituationChipSx,
  INSIGHT_PANEL_COMPACT,
  INSIGHT_PANEL_NARROW,
  READABLE_BODY_SX,
  READABLE_TITLE_SX,
  analysisBg,
} from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import type { InsightChartItem } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";

interface InsightStatusPillsProps {
  sectionTitle?: string;
  insightText?: string;
  items: InsightChartItem[];
  selectedKeys: string[];
  accentColor: string;
  totalForPercent: number;
  patientCountByKey: Record<string, number>;
  itemUnitLabel: string;
  onToggle: (key: string) => void;
  disabled?: boolean;
}

export function InsightStatusPills({
  sectionTitle = "Situação",
  insightText,
  items,
  selectedKeys,
  accentColor,
  totalForPercent,
  patientCountByKey,
  itemUnitLabel,
  onToggle,
  disabled = false,
}: InsightStatusPillsProps) {
  const maxValue = useMemo(
    () => Math.max(...items.map((item) => item.value), 1),
    [items]
  );

  const handleKeyDown = (event: KeyboardEvent, key: string) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle(key);
    }
  };

  const titleNode = (
    <Typography fontWeight={700} sx={{ ...READABLE_TITLE_SX, mb: 0.75 }}>
      {sectionTitle}
    </Typography>
  );

  return (
    <Box>
      {insightText ? (
        <Tooltip title={insightText} arrow placement="top">
          <Box component="span" sx={{ display: "inline-block" }}>
            {titleNode}
          </Box>
        </Tooltip>
      ) : (
        titleNode
      )}

      <Box
        className="insight-status-compact"
        sx={{
          display: "none",
          flexWrap: "wrap",
          gap: 1,
          mb: 0.25,
          [INSIGHT_PANEL_NARROW]: { display: "flex" },
        }}
      >
        {items.map((item) => {
          const selected = selectedKeys.includes(item.key);
          const patients = patientCountByKey[item.key] ?? 0;
          const pct =
            totalForPercent > 0 ? Math.round((item.value / totalForPercent) * 100) : 0;

          return (
            <Tooltip
              key={item.key}
              title={`${item.value} ${itemUnitLabel} · ${patients} pacientes · ${pct}%`}
              arrow
            >
              <Box
                component="button"
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => !disabled && onToggle(item.key)}
                onKeyDown={(e) => handleKeyDown(e, item.key)}
                title={item.label}
                sx={{
                  ...insightSituationChipSx(accentColor, selected),
                  flexShrink: 0,
                  cursor: disabled ? "default" : "pointer",
                  font: "inherit",
                  opacity: disabled ? 0.45 : 1,
                  transition: "opacity 0.15s ease, background-color 0.15s ease",
                  "&:hover": {
                    borderColor: accentColor,
                    bgcolor: selected ? accentColor : analysisBg(accentColor, 0.12),
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${alpha(accentColor, 0.5)}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {item.label}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      <Stack
        className="insight-status-full"
        spacing={1.25}
        sx={{
          [INSIGHT_PANEL_NARROW]: { display: "none" },
        }}
      >
        {items.map((item) => {
          const pct =
            totalForPercent > 0 ? Math.round((item.value / totalForPercent) * 100) : 0;
          const selected = selectedKeys.includes(item.key);
          const patients = patientCountByKey[item.key] ?? 0;
          const barWidth = `${Math.max(8, (item.value / maxValue) * 100)}%`;

          return (
            <Tooltip
              key={item.key}
              title={`${item.value} ${itemUnitLabel} · ${patients} pacientes`}
              arrow
            >
              <Box
                component="button"
                type="button"
                onClick={() => !disabled && onToggle(item.key)}
                onKeyDown={(e) => handleKeyDown(e, item.key)}
                sx={{
                  ...insightMetricRowLayoutSx,
                  display: "flex",
                  width: "100%",
                  [INSIGHT_PANEL_COMPACT]: { px: 1.25, py: 1 },
                  textAlign: "left",
                  border: "1px solid",
                  borderColor: selected ? accentColor : alpha(accentColor, 0.35),
                  borderRadius: 2,
                  px: 1.5,
                  py: 1.15,
                  minHeight: 52,
                  cursor: disabled ? "default" : "pointer",
                  pointerEvents: disabled ? "none" : "auto",
                  opacity: disabled ? 0.45 : 1,
                  bgcolor: selected ? accentColor : "background.paper",
                  color: selected ? "#fff" : "text.primary",
                  font: "inherit",
                  transition:
                    "opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease",
                  "&:hover": {
                    borderColor: accentColor,
                    bgcolor: selected ? accentColor : analysisBg(accentColor, 0.1),
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${alpha(accentColor, 0.5)}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selected}
                  tabIndex={-1}
                  icon={
                    <CheckBoxOutlineBlankIcon
                      sx={{ fontSize: 24, color: selected ? "#fff" : alpha(accentColor, 0.55) }}
                    />
                  }
                  checkedIcon={
                    <CheckBoxIcon sx={{ fontSize: 24, color: selected ? "#fff" : accentColor }} />
                  }
                  sx={{ p: 0, color: "inherit" }}
                />
                <Typography
                  className="insight-metric-label"
                  title={item.label}
                  sx={{ color: "inherit", ...READABLE_BODY_SX }}
                >
                  {item.label}
                </Typography>
                <Box
                  className="insight-metric-bar-wrap"
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    bgcolor: selected ? alpha("#fff", 0.25) : alpha(accentColor, 0.12),
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: barWidth,
                      borderRadius: 6,
                      bgcolor: selected ? "#fff" : alpha(accentColor, 0.55),
                    }}
                  />
                </Box>
                <Typography
                  className="insight-metric-pct"
                  fontWeight={700}
                  sx={{ width: 52, textAlign: "right", color: "inherit", ...READABLE_BODY_SX }}
                >
                  {pct}%
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}
